import { useState } from "react";

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { docUrl, type DocPage } from "../lib/docLinks";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { DEFAULT_PAGE_SIZE, nextPageParam } from "../lib/pagination";
import { queryKeys } from "../lib/queryKeys";
import {
  Button,
  HelpTip,
  Inline,
  InlineError,
  Select,
  Stack,
  Text,
  TextInput,
} from "../ui";
import { RowConfirm } from "./shared/RowConfirm";

type IntegrationCredential =
  components["schemas"]["IntegrationCredentialResponse"];
type IntegrationCredentialProvider =
  components["schemas"]["IntegrationCredentialProvider"];

const firstPageToken: string | undefined = undefined;

export interface IntegrationCredentialSelectProps {
  disabled?: boolean;
  onChange: (credentialsRef: string) => void;
  provider: IntegrationCredentialProvider;
  value: string;
}

function credentialLabel(credential: IntegrationCredential): string {
  return `${credential.display_name} (key ...${credential.key_hint})`;
}

export function IntegrationCredentialSelect({
  disabled = false,
  onChange,
  provider,
  value,
}: IntegrationCredentialSelectProps) {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [rotateApiKey, setRotateApiKey] = useState("");
  const [rotateApiSecret, setRotateApiSecret] = useState("");
  const isLiveKit = provider === "livekit";
  const providerName = isLiveKit ? "LiveKit" : "Pipecat Cloud";
  const providerDocPage: DocPage = isLiveKit
    ? "guides/connect-livekit"
    : "guides/connect-pipecat-cloud";
  const queryKey = queryKeys.integrationCredentials.list({ provider });

  const credentials = useInfiniteQuery({
    queryKey,
    initialPageParam: firstPageToken,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await apiClient.GET(
        "/v1/integration-credentials",
        {
          params: {
            query: {
              page_size: DEFAULT_PAGE_SIZE,
              page_token: pageParam,
              provider,
            },
          },
        },
      );
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    getNextPageParam: nextPageParam,
  });

  const createCredential = useMutation({
    mutationFn: async () => {
      const secret = isLiveKit
        ? { api_key: apiKey, api_secret: apiSecret }
        : { api_key: apiKey };
      const { data, error } = await apiClient.POST(
        "/v1/integration-credentials",
        {
          body: {
            display_name: displayName.trim(),
            provider,
            secret,
          },
        },
      );
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (credential) => {
      onChange(credential.credentials_ref);
      setApiKey("");
      setApiSecret("");
      setDisplayName("");
      setIsCreating(false);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.integrationCredentials.all,
      });
    },
  });

  const rotateCredential = useMutation({
    mutationFn: async (credential: IntegrationCredential) => {
      const secret = isLiveKit
        ? { api_key: rotateApiKey, api_secret: rotateApiSecret }
        : { api_key: rotateApiKey };
      const { data, error } = await apiClient.POST(
        "/v1/integration-credentials/{id}:rotate",
        {
          body: { secret },
          params: { path: { id: credential.id } },
        },
      );
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async () => {
      setRotateApiKey("");
      setRotateApiSecret("");
      setIsRotating(false);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.integrationCredentials.all,
      });
    },
  });

  const deleteCredential = useMutation({
    mutationFn: async (credential: IntegrationCredential) => {
      const { error } = await apiClient.DELETE(
        "/v1/integration-credentials/{id}",
        { params: { path: { id: credential.id } } },
      );
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: async () => {
      onChange("");
      setIsConfirmingDelete(false);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.integrationCredentials.all,
      });
    },
  });

  const items = credentials.data?.pages.flatMap((page) => page.items) ?? [];
  const selectedIsLoaded = items.some(
    (credential) => credential.credentials_ref === value,
  );
  const selectedCredential = items.find(
    (credential) => credential.credentials_ref === value,
  );

  return (
    <Stack gap={3}>
      <Select
        help={
          <HelpTip label="About saved credentials">
            Saved secrets are never displayed again. Choose a credential or
            replace its secret. Interfaces store a reference to the credential,
            and new sessions use the latest saved value.
          </HelpTip>
        }
        disabled={disabled || credentials.isPending}
        label={`${providerName} credential`}
        required
        value={value}
        onChange={(event) => {
          onChange(event.currentTarget.value);
        }}
      >
        <option value="">Choose credential</option>
        {!selectedIsLoaded && value.length > 0 ? (
          <option value={value}>Current credential</option>
        ) : null}
        {items.map((credential) => (
          <option key={credential.id} value={credential.credentials_ref}>
            {credentialLabel(credential)}
          </option>
        ))}
      </Select>
      {credentials.isError ? (
        <InlineError>
          {requestErrorDescription(
            credentials.error,
            "Credential request failed.",
          )}
        </InlineError>
      ) : null}
      {credentials.hasNextPage ? (
        <Button
          disabled={credentials.isFetchingNextPage}
          size="sm"
          variant="secondary"
          onClick={() => {
            void credentials.fetchNextPage();
          }}
        >
          {credentials.isFetchingNextPage ? "Loading..." : "Load more"}
        </Button>
      ) : null}

      {selectedCredential && !isCreating && !isRotating ? (
        <Inline gap={2}>
          <Button
            disabled={disabled}
            size="sm"
            variant="secondary"
            onClick={() => {
              setIsRotating(true);
              setIsConfirmingDelete(false);
            }}
          >
            Replace secret
          </Button>
          {/* The §2.12 inline danger confirm: RowConfirm names the target
              credential and keeps the danger Confirm last. */}
          <RowConfirm
            confirmLabel="Confirm delete"
            isConfirming={isConfirmingDelete}
            isPending={deleteCredential.isPending}
            isTriggerDisabled={disabled}
            pendingLabel="Deleting..."
            sentence={`Delete credential ${credentialLabel(selectedCredential)}? Connections using it stop authenticating.`}
            triggerLabel="Delete credential"
            onCancel={() => {
              setIsConfirmingDelete(false);
            }}
            onConfirm={() => {
              deleteCredential.mutate(selectedCredential);
            }}
            onStart={() => {
              setIsConfirmingDelete(true);
            }}
          />
        </Inline>
      ) : null}

      {selectedCredential && isRotating ? (
        <Stack gap={3}>
          <Inline gap={2}>
            <Text variant="muted">The reference stays the same.</Text>
            <HelpTip
              docHref={docUrl(providerDocPage)}
              docLabel={`Connect a ${providerName} agent`}
              label="About credential rotation"
            >
              {
                "Interfaces using this credential keep working. New sessions read the replacement key once this request succeeds."
              }
            </HelpTip>
          </Inline>
          <TextInput
            autoComplete="off"
            label={`New ${providerName} API key`}
            required
            type="password"
            value={rotateApiKey}
            onChange={(event) => {
              setRotateApiKey(event.currentTarget.value);
            }}
          />
          {isLiveKit ? (
            <TextInput
              autoComplete="off"
              label="New LiveKit API secret"
              required
              type="password"
              value={rotateApiSecret}
              onChange={(event) => {
                setRotateApiSecret(event.currentTarget.value);
              }}
            />
          ) : null}
          <Inline gap={2}>
            <Button
              disabled={
                rotateCredential.isPending ||
                rotateApiKey.length === 0 ||
                (isLiveKit && rotateApiSecret.length === 0)
              }
              size="sm"
              onClick={() => {
                rotateCredential.mutate(selectedCredential);
              }}
            >
              {rotateCredential.isPending ? "Rotating..." : "Rotate"}
            </Button>
            <Button
              disabled={rotateCredential.isPending}
              size="sm"
              variant="secondary"
              onClick={() => {
                setIsRotating(false);
                setRotateApiKey("");
                setRotateApiSecret("");
              }}
            >
              Cancel
            </Button>
          </Inline>
        </Stack>
      ) : null}

      {!selectedCredential && !isCreating ? (
        <Button
          disabled={disabled}
          size="sm"
          variant="secondary"
          onClick={() => {
            setIsCreating(true);
          }}
        >
          Add credential
        </Button>
      ) : null}

      {isCreating ? (
        <Stack gap={3}>
          <Text variant="muted">Write-only. Cleared after save.</Text>
          <TextInput
            label="Credential name"
            required
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.currentTarget.value);
            }}
          />
          <TextInput
            autoComplete="off"
            label={`${providerName} API key`}
            required
            type="password"
            value={apiKey}
            onChange={(event) => {
              setApiKey(event.currentTarget.value);
            }}
          />
          {isLiveKit ? (
            <TextInput
              autoComplete="off"
              label="LiveKit API secret"
              required
              type="password"
              value={apiSecret}
              onChange={(event) => {
                setApiSecret(event.currentTarget.value);
              }}
            />
          ) : null}
          <Inline gap={2}>
            <Button
              disabled={
                createCredential.isPending ||
                displayName.trim().length === 0 ||
                apiKey.length === 0 ||
                (isLiveKit && apiSecret.length === 0)
              }
              size="sm"
              onClick={() => {
                createCredential.mutate();
              }}
            >
              {createCredential.isPending ? "Saving..." : "Save credential"}
            </Button>
            <Button
              disabled={createCredential.isPending}
              size="sm"
              variant="secondary"
              onClick={() => {
                setIsCreating(false);
                setApiKey("");
                setApiSecret("");
                setDisplayName("");
              }}
            >
              Cancel
            </Button>
          </Inline>
        </Stack>
      ) : null}

      {createCredential.isError ? (
        <InlineError>
          {requestErrorDescription(
            createCredential.error,
            "Credential request failed.",
          )}
        </InlineError>
      ) : null}
      {rotateCredential.isError ? (
        <InlineError>
          {requestErrorDescription(
            rotateCredential.error,
            "Credential request failed.",
          )}
        </InlineError>
      ) : null}
      {deleteCredential.isError ? (
        <InlineError>
          {requestErrorDescription(
            deleteCredential.error,
            "Credential request failed.",
          )}
        </InlineError>
      ) : null}
    </Stack>
  );
}
